﻿import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/design-system";
import { useAuth } from "@/context/AuthContext";
import { useDanas, danas } from "@/lib/dan";
import { datum } from "@/lib/format";
import {
  dohvatiGradilista,
  type Gradiliste,
  type Raspored,
} from "@/lib/gradilista";
import { workflow, type Crew } from "@/lib/workflow";
import {
  transferError,
  workerTransfers,
  type WorkerTransferResult,
} from "@/lib/workerTransfer";

/** radnikId identifies the person; trenutniRaspored is their current place (or null).
 * zadanoGradilisteId suggests a destination; onDone refreshes the consumer after move/undo. */
export interface WorkerTransferFlowProps {
  triggerLabel?: string;
  radnikId: string;
  trenutniRaspored: Raspored | null;
  zadanoGradilisteId?: string;
  onDone: () => void | Promise<void>;
}
type Notice = {
  result: WorkerTransferResult;
  actor: string;
  onDone: WorkerTransferFlowProps["onDone"];
  error?: string;
};
let notice: Notice | null = null;
const listeners = new Set<() => void>();
function publish(value: Notice | null) {
  notice = value;
  listeners.forEach((fn) => fn());
}
function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
const getNotice = () => notice;

/** One host in AppShell keeps undo available after a moved row disappears. */
export function WorkerTransferNotice() {
  const current = useSyncExternalStore(subscribe, getNotice);
  const { user } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!current) return;
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [current]);
  if (!current || current.actor !== (user?.id ?? "")) return null;
  const remaining = Math.max(
    0,
    Math.min(
      30,
      Math.ceil((Date.parse(current.result.undo_until) - now) / 1000),
    ),
  );
  async function undo() {
    if (!current || busy || !remaining) return;
    setBusy(true);
    try {
      await workerTransfers.undo(current.result.operation_id);
      publish(null);
      await current.onDone();
    } catch (error) {
      publish({ ...current, error: transferError(error) });
    } finally {
      setBusy(false);
    }
  }
  const r = current.result;
  return (
    <aside
      className="fixed bottom-20 left-3 right-3 z-[80] rounded-xl border-2 border-amber-500 bg-white p-4 text-slate-950 shadow-lg md:bottom-5 md:left-auto md:max-w-xl"
      role="status"
      aria-label="Potvrda prebacivanja"
    >
      <p>
        {r.radnik} {r.ceka_odobrenje ? "čeka odobrenje za" : "prebačen na"}{" "}
        {r.gradiliste}, {r.brigada ? `brigada ${r.brigada}` : "bez brigade"}, od{" "}
        {datum(r.od)}
      </p>
      {current.error && <p role="alert">{current.error}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button
          className="min-h-11"
          disabled={!remaining || busy}
          onClick={() => void undo()}
        >
          Poništi{remaining ? ` (${remaining} s)` : ""}
        </Button>
        {!remaining && <span>Vrijeme za poništavanje je isteklo.</span>}
        <Button
          className="min-h-11"
          variant="outline"
          disabled={busy}
          onClick={() => publish(null)}
        >
          Zatvori obavijest
        </Button>
      </div>
    </aside>
  );
}
function nextDay(day: string) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
type Destination = { site: Gradiliste; crew: Crew | null };
export function WorkerTransferFlow({
  triggerLabel,
  radnikId,
  trenutniRaspored,
  zadanoGradilisteId,
  onDone,
}: WorkerTransferFlowProps) {
  const { can, user } = useAuth();
  const today = useDanas();
  const [open, setOpen] = useState(false);
  const [sites, setSites] = useState<Gradiliste[]>([]);
  const [crews, setCrews] = useState<Crew[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [destination, setDestination] = useState<Destination | null>(null);
  const [custom, setCustom] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const operation = useRef("");
  const allowed =
    can("raspored", "create") &&
    (!trenutniRaspored || can("raspored", "update")) &&
    (!trenutniRaspored?.brigada_id || can("brigada", "update"));
  const readCrews = can("brigada", "read");
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    void dohvatiGradilista(true, controller.signal)
      .then(async (available) => {
        const teams = readCrews
          ? await Promise.all(
              available.map((site) =>
                workflow.crews(site.id, controller.signal),
              ),
            )
          : [];
        if (controller.signal.aborted) return;
        const own = (site: Gradiliste) =>
          site.voditelj_korisnik_id === user?.id ||
          site.zamjenik_korisnik_id === user?.id;
        setSites(
          [...available].sort(
            (a, b) =>
              Number(own(b)) - Number(own(a)) ||
              Number(b.id === zadanoGradilisteId) -
                Number(a.id === zadanoGradilisteId) ||
              a.naziv.localeCompare(b.naziv, "hr"),
          ),
        );
        setCrews(teams.flat().filter((c) => c.aktivna));
        setLoaded(true);
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(transferError(e));
      });
    return () => controller.abort();
  }, [open, readCrews, user?.id, zadanoGradilisteId]);
  async function execute(where: Destination, day: string, both: boolean) {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await workerTransfers.move({
        operation_id: operation.current,
        radnik_id: radnikId,
        trenutni_raspored_id: trenutniRaspored?.id ?? null,
        gradiliste_id: where.site.id,
        brigada_id: where.crew?.id ?? null,
        od: day,
        radi_na_oba_danas: both,
      });
      publish({ result, actor: user?.id ?? "", onDone });
      setOpen(false);
      try {
        await onDone();
      } catch {
        publish({
          result,
          actor: user?.id ?? "",
          onDone,
          error:
            "Promjena je potvrđena. Ponovno učitajte popis da vidite novo stanje.",
        });
      }
    } catch (e) {
      setError(transferError(e));
    } finally {
      setBusy(false);
    }
  }
  function choose(where: Destination) {
    operation.current = crypto.randomUUID();
    setDestination(where);
    setCustom(false);
    setError("");
    if (!trenutniRaspored) void execute(where, danas(), false);
  }
  const name = trenutniRaspored?.radnik || "Radnik";
  const oldCrew = crews.find(
    (c) =>
      c.id === trenutniRaspored?.brigada_id ||
      c.clanovi.some(
        (m) =>
          m.radnik_id === radnikId &&
          m.vrijedi_od <= today &&
          (!m.vrijedi_do || m.vrijedi_do >= today) &&
          c.gradiliste_id === trenutniRaspored?.gradiliste_id,
      ),
  );
  const from =
    oldCrew?.naziv ?? trenutniRaspored?.gradiliste ?? "dosadašnje mjesto";
  const crossSite = destination?.site.id !== trenutniRaspored?.gradiliste_id;
  if (!allowed) return null;
  return (
    <>
      <Button
        className="min-h-11"
        variant="outline"
        onClick={() => {
          setOpen(true);
          setLoaded(false);
          setDestination(null);
          setCustom(false);
          setError("");
        }}
      >
        {triggerLabel ?? (trenutniRaspored ? "Prebaci" : "Rasporedi")}
      </Button>
      {open && (
        <Dialog
          title={trenutniRaspored ? `Prebaci — ${name}` : "Rasporedi radnika"}
          onClose={() => {
            if (!busy) setOpen(false);
          }}
        >
          <div className="space-y-4">
            {error && <p role="alert">{error}</p>}
            {!loaded && !error && <p role="status">Učitavanje odredišta…</p>}
            {loaded && !destination && (
              <>
                <h3 className="font-bold">Kamo ide?</h3>
                <p>
                  Vaša gradilišta su na vrhu. Odaberite gradilište i ekipu
                  jednim dodirom.
                </p>
                {!trenutniRaspored && (
                  <p>Odabrani radnik počinje danas na mjestu koje odaberete.</p>
                )}
                <div className="space-y-3">
                  {sites.map((site) => {
                    const teams = crews.filter(
                      (c) => c.gradiliste_id === site.id,
                    );
                    return (
                      <section
                        key={site.id}
                        aria-label={site.naziv}
                        className="rounded-lg border p-3"
                      >
                        <h4 className="mb-2 font-bold">{site.naziv}</h4>
                        <div className="flex flex-wrap gap-2">
                          {teams.length === 1 && can("brigada", "update") && (
                            <Button
                              className="min-h-11"
                              disabled={busy}
                              onClick={() => choose({ site, crew: teams[0]! })}
                            >
                              {site.naziv} · {teams[0]!.naziv}
                            </Button>
                          )}
                          {teams.length > 1 &&
                            can("brigada", "update") &&
                            teams.map((crew) => (
                              <Button
                                className="min-h-11"
                                disabled={busy}
                                key={crew.id}
                                onClick={() => choose({ site, crew })}
                              >
                                {site.naziv} · {crew.naziv}
                              </Button>
                            ))}
                          <Button
                            className="min-h-11"
                            variant="outline"
                            disabled={busy}
                            onClick={() => choose({ site, crew: null })}
                          >
                            {site.naziv} · bez brigade
                          </Button>
                        </div>
                      </section>
                    );
                  })}
                </div>
                {!sites.length && (
                  <p>Nema dostupnih gradilišta. Javite se voditelju.</p>
                )}
              </>
            )}
            {destination && trenutniRaspored && (
              <>
                <h3 className="font-bold">Od kada?</h3>
                <p>
                  {name} prelazi na {destination.site.naziv}
                  {destination.crew
                    ? `, u ${destination.crew.naziv}`
                    : ", bez brigade"}
                  .
                </p>
                <div className="space-y-3">
                  <Button
                    className="min-h-11 w-full whitespace-normal text-left"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void execute(destination, today, crossSite)}
                  >
                    <span>
                      Danas{crossSite ? " (radi na oba mjesta danas)" : ""}
                      <span className="block text-xs">
                        {crossSite
                          ? `Na ${from} završava danas, na novom mjestu počinje danas.`
                          : `U ${from} završava jučer, u novoj ekipi počinje danas.`}
                      </span>
                    </span>
                  </Button>
                  <Button
                    className="min-h-11 w-full whitespace-normal text-left"
                    disabled={busy}
                    onClick={() =>
                      void execute(destination, nextDay(today), false)
                    }
                  >
                    <span>
                      Od sutra
                      <span className="ml-2 text-xs">zadano</span>
                      <span className="block text-xs">
                        U {from} završava danas, na novom mjestu počinje sutra.
                      </span>
                    </span>
                  </Button>
                  <Button
                    className="min-h-11"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setCustom(true)}
                  >
                    Ostalo…
                  </Button>
                  {custom && (
                    <label className="block">
                      Odaberite datum — promjena se potvrđuje odabirom
                      <input
                        className="mt-2 min-h-11 w-full rounded border p-2"
                        type="date"
                        autoFocus
                        min={today}
                        disabled={busy}
                        onChange={(e) => {
                          if (
                            /^\d{4}-\d{2}-\d{2}$/.test(e.target.value) &&
                            e.target.value >= today
                          )
                            void execute(destination, e.target.value, false);
                        }}
                      />
                    </label>
                  )}
                </div>
                <Button
                  className="min-h-11"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setDestination(null)}
                >
                  Drugo odredište
                </Button>
              </>
            )}
            {busy && <p role="status">Potvrđujem promjenu…</p>}
          </div>
        </Dialog>
      )}
    </>
  );
}
